import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Alert, Select, MenuItem,
  FormControl, InputLabel, Chip, CircularProgress, IconButton, Tooltip,
  LinearProgress, Grid, Card, CardContent, Divider,
  List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudDownload from '@mui/icons-material/CloudDownload';
import Description from '@mui/icons-material/Description';
import Clear from '@mui/icons-material/Clear';
import InterfaceHeader from '../components/InterfaceHeader';
import { API_ENDPOINTS } from '../config/api';
import { TOOL_COLORS } from '../constants/colors';
import { useProcess } from '../context/ProcessContext';
import { useActivityLog } from '../context/ActivityLogContext';
import { useDropzone } from 'react-dropzone';
import { FaExchangeAlt, FaFileAlt } from 'react-icons/fa';

const FORMAT_LABELS = {
  // Images
  '.png': 'PNG', '.jpg': 'JPEG', '.jpeg': 'JPEG', '.bmp': 'BMP', '.gif': 'GIF',
  '.tiff': 'TIFF', '.tif': 'TIFF', '.heic': 'HEIC', '.webp': 'WebP',
  // Spreadsheets
  '.xlsx': 'Excel', '.xls': 'Excel (Legacy)', '.csv': 'CSV',
  // Documents
  '.pdf': 'PDF', '.txt': 'Text', '.md': 'Markdown', '.html': 'HTML',
  // Data
  '.json': 'JSON', '.xml': 'XML', '.yaml': 'YAML', '.yml': 'YAML',
  // Archives
  '.zip': 'ZIP', '.tar': 'TAR', '.gz': 'GZ', '.7z': '7Z', '.rar': 'RAR',
  // Audio
  '.mp3': 'MP3', '.wav': 'WAV', '.ogg': 'OGG', '.flac': 'FLAC', '.aac': 'AAC',
  // Video
  '.mp4': 'MP4', '.mov': 'MOV', '.avi': 'AVI', '.webm': 'WebM', '.mkv': 'MKV',
  // 3D
  '.stl': 'STL', '.obj': 'OBJ', '.ply': 'PLY', '.fbx': 'FBX',
  '.gltf': 'glTF', '.glb': 'GLB', '.3mf': '3MF',
};

const FORMAT_COLORS = {
  // Images
  '.png': '#3B82F6', '.jpg': '#f59e0b', '.jpeg': '#f59e0b', '.bmp': '#8b5cf6', '.gif': '#ec4899',
  '.tiff': '#7c3aed', '.tif': '#7c3aed', '.heic': '#6366f1', '.webp': '#14b8a6',
  // Spreadsheets
  '.xlsx': '#228B22', '.xls': '#228B22', '.csv': '#228B22',
  // Documents
  '.pdf': '#DC143C', '.txt': '#6b7280', '.md': '#1e40af', '.html': '#e34c26',
  // Data
  '.json': '#f59e0b', '.xml': '#e34c26', '.yaml': '#cb171e', '.yml': '#cb171e',
  // Archives
  '.zip': '#f59e0b', '.tar': '#8b5cf6', '.gz': '#6366f1', '.7z': '#16a34a', '.rar': '#dc2626',
  // Audio
  '.mp3': '#1DB954', '.wav': '#3B82F6', '.ogg': '#f97316', '.flac': '#8b5cf6', '.aac': '#06b6d4',
  // Video
  '.mp4': '#ef4444', '.mov': '#3B82F6', '.avi': '#f59e0b', '.webm': '#16a34a', '.mkv': '#8b5cf6',
  // 3D
  '.stl': '#06b6d4', '.obj': '#f97316', '.ply': '#8b5cf6', '.fbx': '#16a34a',
  '.gltf': '#3B82F6', '.glb': '#1e40af', '.3mf': '#ec4899',
};

const CONVERSIONS = {
  // Images
  '.png': ['.jpg', '.bmp', '.gif', '.tiff', '.pdf'],
  '.jpg': ['.png', '.bmp', '.gif', '.tiff', '.pdf'],
  '.jpeg': ['.png', '.bmp', '.gif', '.tiff', '.pdf'],
  '.bmp': ['.png', '.jpg', '.gif', '.tiff', '.pdf'],
  '.gif': ['.png', '.jpg', '.bmp', '.tiff', '.pdf'],
  '.tiff': ['.png', '.jpg', '.bmp', '.gif', '.pdf'],
  '.tif': ['.png', '.jpg', '.bmp', '.gif', '.pdf'],
  '.heic': ['.png', '.jpg', '.pdf'],
  '.webp': ['.png', '.jpg', '.pdf'],
  // Spreadsheets
  '.xlsx': ['.csv', '.pdf', '.json'],
  '.xls': ['.csv', '.pdf', '.json'],
  '.csv': ['.xlsx', '.pdf', '.json'],
  // Documents
  '.txt': ['.pdf'],
  '.md': ['.pdf', '.html'],
  // Data
  '.json': ['.csv', '.xlsx', '.xml', '.yaml'],
  '.xml': ['.json'],
  '.yaml': ['.json'],
  '.yml': ['.json'],
  // Archives
  '.zip': ['.tar', '.7z'],
  '.tar': ['.zip'],
  '.gz': ['.zip'],
  '.7z': ['.zip'],
  '.rar': ['.zip'],
  // Audio
  '.mp3': ['.wav', '.ogg', '.flac', '.aac'],
  '.wav': ['.mp3', '.ogg', '.flac', '.aac'],
  '.ogg': ['.mp3', '.wav', '.flac'],
  '.flac': ['.mp3', '.wav', '.ogg'],
  '.aac': ['.mp3', '.wav'],
  // Video
  '.mp4': ['.mov', '.avi', '.webm', '.mkv', '.gif'],
  '.mov': ['.mp4', '.avi', '.webm', '.mkv'],
  '.avi': ['.mp4', '.mov', '.webm', '.mkv'],
  '.webm': ['.mp4', '.mov'],
  '.mkv': ['.mp4', '.mov'],
  // 3D
  '.stl': ['.obj', '.ply', '.fbx'],
  '.obj': ['.stl', '.ply', '.fbx'],
  '.ply': ['.stl', '.obj'],
  '.fbx': ['.obj', '.stl'],
  '.gltf': ['.obj', '.stl'],
  '.glb': ['.obj', '.stl'],
  '.3mf': ['.stl', '.obj'],
};

const FileConverter = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { addProcess, updateProcess, removeProcess } = useProcess();
  const { addActivityProcess, completeActivityProcess, setLogs: setActivityLogs } = useActivityLog();

  const [files, setFiles] = useState([]);
  const [targetFormat, setTargetFormat] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState(null);

  const sourceExt = files.length > 0 ? ('.' + files[0].name.split('.').pop().toLowerCase()) : '';
  const availableTargets = sourceExt ? (CONVERSIONS[sourceExt] || []) : [];

  const onDrop = useCallback((accepted) => {
    if (!accepted.length) return;
    const ext = '.' + accepted[0].name.split('.').pop().toLowerCase();
    if (!CONVERSIONS[ext]) {
      setError(`Unsupported file format: ${ext}. Supported: ${Object.keys(CONVERSIONS).join(', ')}`);
      return;
    }
    const sameType = accepted.filter(f => ('.' + f.name.split('.').pop().toLowerCase()) === ext);
    if (sameType.length !== accepted.length) {
      setError('All files must be the same format. Mixed types were dropped.');
      return;
    }
    setFiles(prev => {
      const prevExt = prev.length > 0 ? ('.' + prev[0].name.split('.').pop().toLowerCase()) : '';
      if (prevExt && prevExt !== ext) return sameType;
      const existingNames = new Set(prev.map(f => f.name));
      const newFiles = sameType.filter(f => !existingNames.has(f.name));
      return [...prev, ...newFiles];
    });
    setTargetFormat(prev => {
      if (prev && CONVERSIONS[ext]?.includes(prev)) return prev;
      return CONVERSIONS[ext]?.length === 1 ? CONVERSIONS[ext][0] : '';
    });
    setError(null);
    setSuccess(null);
    setResults(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, multiple: true, noClick: loading,
  });

  const handleConvert = async () => {
    if (!files.length || !targetFormat) return;
    setLoading(true); setError(null); setSuccess(null); setResults(null);
    setProgress({ current: 0, total: files.length });

    const activityId = `convert-${Date.now()}`;
    const uiId = addProcess({ name: 'File Converter' });
    addActivityProcess(activityId, `Converting ${files.length} file${files.length > 1 ? 's' : ''} to ${targetFormat}`, 'File Converter');

    let successCount = 0;
    let failCount = 0;
    const logs = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const outputName = file.name.replace(/\.[^.]+$/, targetFormat);
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('targetFormat', targetFormat);

        const resp = await fetch(API_ENDPOINTS.FILE_CONVERTER_CONVERT, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` },
          body: formData,
        });

        if (!resp.ok) {
          const errData = await resp.json().catch(() => ({}));
          throw new Error(errData.message || `Failed (${resp.status})`);
        }

        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = outputName;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
        successCount++;
        logs.push(`Converted: ${file.name} → ${outputName}`);
      } catch (err) {
        failCount++;
        logs.push(`Error: ${file.name} — ${err.message}`);
      }
      setProgress({ current: i + 1, total: files.length });
      setActivityLogs(activityId, logs);
    }

    logs.push(failCount > 0
      ? `Completed: ${successCount} converted, ${failCount} failed`
      : `Successfully finished — ${successCount} file${successCount !== 1 ? 's' : ''} converted`);
    setActivityLogs(activityId, logs);

    setLoading(false);
    setResults({ success: successCount, failed: failCount, total: files.length });
    if (successCount > 0) {
      setSuccess(`Converted ${successCount}/${files.length} file${files.length > 1 ? 's' : ''}${failCount > 0 ? ` (${failCount} failed)` : ''}`);
      updateProcess(uiId, { status: 'success' });
      setTimeout(() => removeProcess(uiId), 3000);
      completeActivityProcess(activityId, 'success', `${successCount} file${successCount !== 1 ? 's' : ''} converted`);
    } else {
      setError('All conversions failed');
      updateProcess(uiId, { status: 'error' });
      completeActivityProcess(activityId, 'error', 'All conversions failed');
    }
  };

  const resetAll = () => {
    setFiles([]); setTargetFormat(''); setError(null); setSuccess(null); setResults(null);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <InterfaceHeader title="File Converter" color={TOOL_COLORS.OTHER.primary} category="other" />

        <Grid container spacing={3}>
          {/* Main Processing Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Upload & Convert
              </Typography>

              <Chip icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label="Files are converted locally — nothing is uploaded or stored on our servers"
                size="small" sx={{ mb: 3, fontSize: 11, fontWeight: 500, bgcolor: isDark ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.08)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.2)' }} />

              {/* File Upload Area */}
              <Card sx={{ mb: 3, bgcolor: 'transparent' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Description sx={{ color: TOOL_COLORS.OTHER.primary }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      Select Files
                    </Typography>
                    <Chip label="Same type only" size="small" variant="outlined" sx={{ fontSize: 11 }} />
                  </Box>

                  <Box {...getRootProps()} sx={{
                    border: `2px dashed ${isDragActive ? TOOL_COLORS.OTHER.primary : files.length > 0 ? '#4CAF50' : theme.palette.divider}`,
                    borderRadius: 2, p: 3, textAlign: 'center', cursor: 'pointer', transition: 'all 0.3s ease', mb: 2,
                    bgcolor: isDragActive ? `${TOOL_COLORS.OTHER.primary}10` : files.length > 0 ? (isDark ? 'rgba(76,175,80,0.15)' : 'rgba(76,175,80,0.06)') : 'transparent',
                    '&:hover': { borderColor: files.length > 0 ? '#4CAF50' : TOOL_COLORS.OTHER.primary, bgcolor: files.length > 0 ? (isDark ? 'rgba(76,175,80,0.2)' : 'rgba(76,175,80,0.1)') : `${TOOL_COLORS.OTHER.primary}10` },
                  }}>
                    <input {...getInputProps()} />
                    {!files.length ? (
                      <>
                        <CloudDownload sx={{ fontSize: 48, color: isDragActive ? TOOL_COLORS.OTHER.primary : 'text.secondary', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>
                          {isDragActive ? 'Drop files here!' : 'Drop files here or click to browse'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          PNG, JPG, BMP, GIF, HEIC, WebP, Excel, CSV — select multiple files of the same type
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>
                          {files.length} file{files.length > 1 ? 's' : ''} selected
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Click or drop to add more {FORMAT_LABELS[sourceExt] || sourceExt} files</Typography>
                      </>
                    )}
                  </Box>

                  {/* Selected files list */}
                  {files.map((f, i) => (
                    <Box key={f.name + i} sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      p: 1, mb: 0.5, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, borderRadius: 1,
                      '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)', borderColor: TOOL_COLORS.OTHER.primary },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        <Description sx={{ color: FORMAT_COLORS[sourceExt] || TOOL_COLORS.OTHER.primary, fontSize: 20 }} />
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>({(f.size / 1024).toFixed(1)} KB)</Typography>
                      </Box>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setFiles(prev => { const next = prev.filter((_, j) => j !== i); if (!next.length) { setTargetFormat(''); setSuccess(null); setResults(null); } return next; }); }}
                        sx={{ color: theme.palette.error.main }}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </CardContent>
              </Card>

              {/* Target Format Selection + Convert */}
              {files.length > 0 && (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Chip label={FORMAT_LABELS[sourceExt] || sourceExt.toUpperCase()} sx={{ fontWeight: 700, bgcolor: (FORMAT_COLORS[sourceExt] || '#6b7280') + '20', color: FORMAT_COLORS[sourceExt] || '#6b7280', height: 36, fontSize: 13 }} />
                  <SwapHorizIcon sx={{ color: theme.palette.text.secondary, fontSize: 28 }} />
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Convert to</InputLabel>
                    <Select value={targetFormat} label="Convert to" onChange={(e) => setTargetFormat(e.target.value)}>
                      {availableTargets.map(fmt => (
                        <MenuItem key={fmt} value={fmt}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: FORMAT_COLORS[fmt] || '#6b7280' }} />
                            {FORMAT_LABELS[fmt] || fmt}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button variant="contained" onClick={handleConvert} disabled={loading || !targetFormat}
                    startIcon={loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <FaExchangeAlt />}
                    sx={{ bgcolor: TOOL_COLORS.OTHER.primary, '&:hover': { bgcolor: TOOL_COLORS.OTHER.hover }, px: 4, textTransform: 'none', fontWeight: 600 }}>
                    {loading ? `Converting ${progress.current}/${progress.total}...` : `Convert${files.length > 1 ? ` ${files.length} Files` : ''}`}
                  </Button>
                  {(files.length > 0 || results) && !loading && (
                    <Button variant="outlined" onClick={resetAll}
                      sx={{ borderColor: theme.palette.divider, color: theme.palette.text.primary, textTransform: 'none',
                        '&:hover': { borderColor: theme.palette.text.secondary } }}>
                      Clear
                    </Button>
                  )}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Status & Downloads Section */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Downloads
              </Typography>

              {/* Progress */}
              {loading && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Converting files...</Typography>
                  <LinearProgress variant="determinate" value={progress.total > 0 ? (progress.current / progress.total) * 100 : 0}
                    sx={{ height: 8, borderRadius: 4,
                      backgroundColor: isDark ? 'rgba(255,140,0,0.2)' : 'rgba(255,140,0,0.1)',
                      '& .MuiLinearProgress-bar': { backgroundColor: TOOL_COLORS.OTHER.primary, borderRadius: 4 } }} />
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.5, display: 'block' }}>
                    {progress.current}/{progress.total} files
                  </Typography>
                </Box>
              )}

              {/* Error Alert */}
              {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

              {/* Success Alert */}
              {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>}

              {/* Results */}
              {results && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Processing Results:</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><FaExchangeAlt color={TOOL_COLORS.OTHER.primary} /></ListItemIcon>
                      <ListItemText primary={`${results.success} File${results.success !== 1 ? 's' : ''} Converted`}
                        secondary="Converted and auto-downloaded" />
                    </ListItem>
                    {results.failed > 0 && (
                      <ListItem>
                        <ListItemIcon><FaFileAlt color="#f44336" /></ListItemIcon>
                        <ListItemText primary={`${results.failed} Failed`} secondary="Check file format compatibility" />
                      </ListItem>
                    )}
                  </List>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Converted files have been automatically downloaded. Check your downloads folder.
                  </Alert>
                </Box>
              )}

              {!loading && !results && !error && (
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                  Select files and a target format to start converting.
                </Typography>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Supported Formats */}
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>Supported Formats:</Typography>
              {[
                { label: 'Images', formats: ['.png', '.jpg', '.bmp', '.gif', '.tiff', '.heic', '.webp'] },
                { label: 'Spreadsheets', formats: ['.xlsx', '.xls', '.csv'] },
                { label: 'Documents', formats: ['.txt', '.md', '.pdf', '.html'] },
                { label: 'Data', formats: ['.json', '.xml', '.yaml'] },
                { label: 'Archives', formats: ['.zip', '.tar', '.gz', '.7z', '.rar'] },
                { label: 'Audio', formats: ['.mp3', '.wav', '.ogg', '.flac', '.aac'] },
                { label: 'Video', formats: ['.mp4', '.mov', '.avi', '.webm', '.mkv'] },
                { label: '3D Models', formats: ['.stl', '.obj', '.ply', '.fbx', '.gltf', '.glb', '.3mf'] },
              ].map(group => (
                <Box key={group.label} sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.text.secondary, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>{group.label}</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {group.formats.map(f => (
                      <Chip key={f} label={FORMAT_LABELS[f] || f} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 600, bgcolor: (FORMAT_COLORS[f] || '#6b7280') + '15', color: FORMAT_COLORS[f] || '#6b7280' }} />
                    ))}
                  </Box>
                </Box>
              ))}

              <Divider sx={{ my: 3 }} />

              {/* Description */}
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                Universal file converter supporting images, spreadsheets, documents, data formats, audio, video, 3D models, and archives. Select multiple files of the same type to batch convert. Audio/video requires FFmpeg on the server. 3D models use Assimp.
              </Typography>

              <Divider sx={{ my: 3 }} />

              {/* Instructions */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Instructions:</Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                1. Drop or select files (multiple of the same type)<br />
                2. Choose target format from the dropdown<br />
                3. Click "Convert" — files auto-download<br />
                4. No data is stored on our servers
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default FileConverter;
